import { z } from "zod";
import {
  FIELD_EMAIL_VALID,
  FIELD_REQUIRED,
} from "../../../i18n/keysTranslations";
import { asRadioString, asString, asStringArray } from "./schemaHelpers";
import { validateModulesFromRegistry } from "./validateModulesFromRegistry";

/**
 * Creates the enterprise intake validation schema.
 */
export const createEnterpriseIntakeSchema = (t) =>
  z
    .object({
      // Section 1
      fullName: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) }),
      corporateEmail: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) })
        .email({ message: t(FIELD_EMAIL_VALID) }),
      companyName: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) }),
      jobTitle: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) }),
      companyWebsite: z.preprocess(asString, z.string()),

      purchaseDecisionMaker: z.preprocess(
        asRadioString,
        z.string().min(1, { message: t(FIELD_REQUIRED) })
      ),
      currentToolOrAdleStatus: z.preprocess(
        asRadioString,
        z.string().min(1, { message: t(FIELD_REQUIRED) })
      ),
      currentToolOrAdleStatusOther: z.preprocess(asString, z.string()),
      operatingCountriesCount: z.preprocess(asString, z.string()).refine(
        (val) => {
          const num = parseInt(val, 10);
          return !isNaN(num) && num >= 1 && String(num) === val.trim();
        },
        { message: t(FIELD_REQUIRED) }
      ),
      creativesVaryByMarket: z.preprocess(asString, z.string()),

      // Section 2 — module selector
      selectedAdleModules: z.preprocess(asStringArray, z.array(z.string())),
      primaryGoal: z.preprocess(
        asRadioString,
        z.string().min(1, { message: t(FIELD_REQUIRED) })
      ),
      primaryGoalOther: z.preprocess(asString, z.string()),

      // Module 1
      module1BrandsOrVisualIdentitiesCount: z.preprocess(asString, z.string()),
      module1IndependentBrandConfigsCount: z.preprocess(asString, z.string()),
      module1MonthlyNewImagesCount: z.preprocess(asString, z.string()),
      module1FormatsPerPieceAvg: z.preprocess(asString, z.string()),
      module1MonthlyAdditionalEditsCount: z.preprocess(asString, z.string()),
      module1MonthlyVideosCount: z.preprocess(asString, z.string()),
      module1MonthlyImageAnimationsCount: z.preprocess(asString, z.string()),
      module1VersionsPerPieceBeforeApproval: z.preprocess(asString, z.string()),
      module1CreativeVolumeVariation: z.preprocess(asString, z.string()),
      module1PeakMonthPiecesCount: z.preprocess(asString, z.string()),

      // Module 2
      module2AdAccountsOrAdvertisersCount: z.preprocess(asString, z.string()),
      module2MonthlyCampaignsCount: z.preprocess(asString, z.string()),
      module2MonthlyOptimizationsCount: z.preprocess(asString, z.string()),
      module2CampaignVolumeVariation: z.preprocess(asString, z.string()),
      module2PeakMonthCampaignsCount: z.preprocess(asString, z.string()),

      // Module 3
      module3CampaignsToImportCount: z.preprocess(asString, z.string()),
      module3ImportPlatforms: z.preprocess(asStringArray, z.array(z.string())),
      module3ImportPlatformsOther: z.preprocess(asString, z.string()),

      // Module 4
      module4MonthlyOrganicPostsCount: z.preprocess(asString, z.string()),
      module4SchedulingNetworks: z.preprocess(
        asStringArray,
        z.array(z.string())
      ),
      module4SchedulingNetworksOther: z.preprocess(asString, z.string()),
      module4PostCreationSource: z.preprocess(asString, z.string()),

      // Relocated to Module 2 (optional at schema level; required via registry)
      phase1PaidChannels: z.preprocess(asStringArray, z.array(z.string())),
      phase1PaidChannelsOther: z.preprocess(asString, z.string()),
      monthlyAdSpendRange: z.preprocess(asString, z.string()),
      monthlyAdSpendRangeOther: z.preprocess(asString, z.string()),
      hasAdminAccessPhase1: z.preprocess(asString, z.string()),
      hasAdminAccessPhase1Other: z.preprocess(asString, z.string()),

      // Legacy advertiser/credit fields (sections removed; kept for RTDB compat)
      activeAdvertisersMonth1: z.preprocess(asString, z.string()),
      activeAdvertisersMonth1Other: z.preprocess(asString, z.string()),
      totalAdvertisers60to90Days: z.preprocess(asString, z.string()),
      totalAdvertisers60to90DaysOther: z.preprocess(asString, z.string()),

      // Section 3 — Users
      platformUsersCount: z.preprocess(asString, z.string()).refine(
        (val) => {
          const num = parseInt(val, 10);
          return !isNaN(num) && num >= 1 && String(num) === val.trim();
        },
        { message: t(FIELD_REQUIRED) }
      ),
      creditsManagementModel: z.preprocess(
        asRadioString,
        z.string().min(1, { message: t(FIELD_REQUIRED) })
      ),
      creditsManagementModelOther: z.preprocess(asString, z.string()),

      // Section 5 (reduced)
      monthlyEstimatedCredits: z.preprocess(asString, z.string()),
      monthlyEstimatedCreditsOther: z.preprocess(asString, z.string()),

      // Section 4 — Support, Contract & Implementation
      platformManagementModel: z.preprocess(asString, z.string()),
      supportLevel: z.preprocess(asString, z.string()),
      supportLevelOther: z.preprocess(asString, z.string()),
      csmCadence: z.preprocess(asString, z.string()),
      csmCadenceOther: z.preprocess(asString, z.string()),
      contractFormat: z.preprocess(asString, z.string()),
      contractFormatOther: z.preprocess(asString, z.string()),
      implementationTimeline: z.preprocess(asString, z.string()),
      implementationTimelineOther: z.preprocess(asString, z.string()),
      whiteLabelRequirement: z.preprocess(asString, z.string()),
      urgencyDeadlineOrEvent: z.preprocess(asString, z.string()),
      estimatedToolBudgetRange: z.preprocess(asString, z.string()),
      securityRequirements: z.preprocess(asStringArray, z.array(z.string())),
      securityRequirementsOther: z.preprocess(asString, z.string()),

      // Section 5 — Additional Notes
      additionalNotes: z.preprocess(asString, z.string()),
    })
    .superRefine((data, ctx) => {
      validateModulesFromRegistry({ data, ctx, t });

      const operatingCountriesCount = parseInt(
        data.operatingCountriesCount,
        10
      );
      if (
        !isNaN(operatingCountriesCount) &&
        operatingCountriesCount > 1 &&
        !data.creativesVaryByMarket?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(FIELD_REQUIRED),
          path: ["creativesVaryByMarket"],
        });
      }
    });
